import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { ErrorState } from './ui/empty';

/**
 * Catches render-time throws inside a route so one broken screen doesn't take
 * the whole app down. Query/mutation errors are handled by TanStack Query and
 * never reach here — this is for genuine programming errors.
 *
 * Keyed on the current pathname by the caller, so navigating away clears it.
 */
export class RouteErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced in the console rather than swallowed; wire to Sentry here when
    // the v2 stack has it.
    console.error('Route crashed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto w-full max-w-6xl px-6 py-8">
        <ErrorState
          title="This screen hit a problem"
          message={this.state.error.message}
        />
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            <RefreshCw />
            Reload the app
          </Button>
        </div>
      </div>
    );
  }
}
